// src/context/CartContext.js
import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import cartService from '../services/cartService';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!localStorage.getItem('access_token');
  };

  // Fetch cart from backend
  const fetchCart = useCallback(async () => {
    if (!isAuthenticated()) {
      setCart(null);
      setCartCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.getCart();

      // Deep clone to ensure new object references for React to detect changes
      const newCart = response.cart ? JSON.parse(JSON.stringify(response.cart)) : null;
      setCart(newCart);
      setCartCount(newCart?.total_items || 0);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize cart on mount
  useEffect(() => {
    if (isAuthenticated()) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Add medicine to cart
  const addMedicine = async (medicineId, quantity = 1) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to add items to cart');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.addMedicine(medicineId, quantity);

      // Update cart with response
      setCart(response.cart);
      setCartCount(response.cart?.total_items || 0);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add medical test to cart
  const addMedicalTest = async (testId, quantity = 1) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to add items to cart');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.addMedicalTest(testId, quantity);
      setCart(response.cart);
      setCartCount(response.cart?.total_items || 0);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add medical package to cart
  const addMedicalPackage = async (packageId, quantity = 1) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to add items to cart');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.addMedicalPackage(packageId, quantity);
      setCart(response.cart);
      setCartCount(response.cart?.total_items || 0);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add paramedical service to cart
  const addParamedicalService = async (serviceId, quantity = 1) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to add items to cart');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.addParamedicalService(serviceId, quantity);
      setCart(response.cart);
      setCartCount(response.cart?.total_items || 0);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update item quantity
  const updateQuantity = async (itemType, itemId, quantity) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to update cart');
    }

    try {
      setError(null);
      const response = await cartService.updateQuantity(itemType, itemId, quantity);

      // Use the cart from the update response if available
      if (response.cart) {
        const newCart = JSON.parse(JSON.stringify(response.cart));
        setCart(newCart);
        setCartCount(newCart?.total_items || 0);
      } else {
        // Fallback: Refetch cart if response doesn't include it
        await fetchCart();
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Remove item from cart
  const removeItem = async (itemType, itemId) => {
    if (!isAuthenticated()) {
      throw new Error('Please login to modify cart');
    }

    try {
      setError(null);
      const response = await cartService.removeItem(itemType, itemId);

      // Use the cart from the remove response if available
      if (response.cart) {
        const newCart = JSON.parse(JSON.stringify(response.cart));
        setCart(newCart);
        setCartCount(newCart?.total_items || 0);
      } else {
        // Fallback: Refetch cart if response doesn't include it
        await fetchCart();
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!isAuthenticated()) {
      throw new Error('Please login to clear cart');
    }

    try {
      setLoading(true);
      setError(null);
      await cartService.clearCart();
      setCart(null);
      setCartCount(0);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Checkout cart - create order from cart items
  const checkout = async (addressId, paymentMethod = 'online') => {
    if (!isAuthenticated()) {
      throw new Error('Please login to checkout');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await cartService.checkout(addressId, paymentMethod);
      
      // Clear local cart after successful checkout
      await fetchCart();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    cartCount,
    loading,
    error,
    fetchCart,
    addMedicine,
    addMedicalTest,
    addMedicalPackage,
    addParamedicalService,
    updateQuantity,
    removeItem,
    clearCart,
    checkout,
    setCart,
    isAuthenticated,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
